import { CompoundingPeriod, VoucherCategory, VoucherTypeSetting, } from '../../vouchers/index.js';
import { parseDate } from '../utils.js';
export const ONE_DAY_IN_MS = 1000 * 60 * 60 * 24;
// Open-ended sentinel; finite because Infinity doesn't survive the
// calculation's JSON clone.
export const OPEN_ENDED = Number.MAX_SAFE_INTEGER;
/**
 * Number of distinct UTC calendar months that the half-open period
 * [from, to) overlaps with — used for "renter per påbegyndt måned":
 * any month the period touches counts as 1, even if only partially.
 *
 * - `from` is inclusive, `to` is exclusive (ms epoch). A `to` landing
 *   exactly on a month boundary is treated as still belonging to the
 *   previous month, so it is not counted as starting the next one.
 * - Returns 0 for zero-length or inverted periods.
 *
 * Examples:
 *   2026-01-15 → 2026-01-20         = 1
 *   2026-01-31 23:59 → 2026-02-01   = 2
 *   2026-01-15 → 2026-02-01 (exact) = 1
 *   from === to                     = 0
 */
export const startedMonthsBetween = (from, to) => {
    if (to <= from) {
        return 0;
    }
    const start = new Date(from);
    const endInclusive = new Date(to - 1);
    return ((endInclusive.getUTCFullYear() - start.getUTCFullYear()) * 12 +
        (endInclusive.getUTCMonth() - start.getUTCMonth()) +
        1);
};
export const getCurrentInterest = (caseVoucher, currentTime = Date.now()) => {
    if (caseVoucher.setting !== VoucherTypeSetting.InterestFromTable) {
        return caseVoucher.interest ?? undefined;
    }
    return caseVoucher.settingDetails.interestRates.reduce((currentRate, interestRate) => {
        if (interestRate.time > currentTime ||
            (currentRate && interestRate.time <= currentRate.time)) {
            return currentRate;
        }
        return interestRate;
    }, undefined)?.rate;
};
export const getLowestCaseInterestRate = (caseVouchers, currentTime = Date.now()) => {
    const interestRates = caseVouchers
        .filter((voucher) => voucher.category === VoucherCategory.INTEREST)
        .map((voucher) => getCurrentInterest(voucher, currentTime))
        .filter((rate) => rate != null);
    if (interestRates.length === 0) {
        return undefined;
    }
    return Math.min(...interestRates);
};
export const formatInterestRate = (interestRate, locale) => interestRate == null
    ? ''
    : new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })
        .format(interestRate)
        .replace(/\s+(?=%)/g, '');
/**
 * @param rates Rates in order (newest to oldest)
 */
export const getInterestRatesGen = (rates) => {
    return (from, to) => {
        const interests = [];
        for (let i = 0; i < rates.length; i++) {
            const rate = rates[i];
            if (!rate.time) {
                throw new Error('Interest rates not set correctly');
            }
            if (rate.time >= to) {
                continue;
            }
            else if (rate.time > from) {
                interests.push(rate);
            }
            else {
                interests.push(rate);
                break;
            }
        }
        return interests.length ? interests.reverse() : [];
    };
};
export const addAmountToVoucher = (caseVoucher, amount, hist) => {
    caseVoucher.amountUpdated = true;
    caseVoucher.amount += amount;
    if (hist) {
        const interestHistory = caseVoucher.interestHistory || [];
        const lastHistItem = interestHistory[interestHistory.length - 1];
        // Either append to previous entry or add new
        if (caseVoucher.interestHistory &&
            lastHistItem &&
            lastHistItem.interest === hist.interest &&
            lastHistItem.referenceVoucherBalance === hist.referenceVoucherBalance &&
            lastHistItem.to === hist.from &&
            lastHistItem.capitalizedInterestBalance ===
                hist.capitalizedInterestBalance) {
            caseVoucher.interestHistory[interestHistory.length - 1] = {
                from: lastHistItem.from,
                to: hist.to,
                amount: lastHistItem.amount + amount,
                interest: lastHistItem.interest,
                referenceVoucherBalance: lastHistItem.referenceVoucherBalance,
                capitalizedInterestBalance: hist.capitalizedInterestBalance,
            };
        }
        else {
            if (caseVoucher.interestHistory != null) {
                caseVoucher.interestHistory.push({
                    ...hist,
                    amount,
                });
            }
            else {
                caseVoucher.interestHistory = [
                    {
                        ...hist,
                        amount,
                    },
                ];
            }
        }
    }
    return caseVoucher;
};
export var BreakpointType;
(function (BreakpointType) {
    BreakpointType["InterestChange"] = "INTEREST_CHANGE";
    BreakpointType["InterestDailyCompoundingPeriod"] = "INTEREST_DAILY_COMPOUNDING_PERIOD";
    BreakpointType["InterestMonthlyCompoundingPeriod"] = "INTEREST_MONTHLY_COMPOUNDING_PERIOD";
    BreakpointType["InterestQuarterlyCompoundingPeriod"] = "INTEREST_QUARTERLY_COMPOUNDING_PERIOD";
    BreakpointType["InterestStartDate"] = "INTEREST_START_DATE";
    BreakpointType["InterestEndDate"] = "INTEREST_END_DATE";
    BreakpointType["InterestPeriodStart"] = "INTEREST_PERIOD_START";
    BreakpointType["InterestPeriodEnd"] = "INTEREST_PERIOD_END";
    BreakpointType["InterestPeriodVoid"] = "INTEREST_PERIOD_VOID";
    // Opt-in reporting boundary (see CalcOptions.monthStartBreakpoints). Never
    // triggers capitalization and never changes the accrued amounts.
    BreakpointType["MonthStart"] = "MONTH_START";
})(BreakpointType || (BreakpointType = {}));
export const COMPOUNDING_PERIOD_TO_BREAKPOINT_TYPE = {
    [CompoundingPeriod.Daily]: BreakpointType.InterestDailyCompoundingPeriod,
    [CompoundingPeriod.Monthly]: BreakpointType.InterestMonthlyCompoundingPeriod,
    [CompoundingPeriod.Quarterly]: BreakpointType.InterestQuarterlyCompoundingPeriod,
};
const getBreakpointsInPeriod = (orderedBreakpoints, from, to) => {
    let breakpoints = [];
    for (let i = 0; i < orderedBreakpoints.length; i++) {
        const breakpoint = orderedBreakpoints[i];
        if (breakpoint.date < from) {
            continue;
        }
        if (breakpoint.date >= to) {
            break;
        }
        breakpoints.push(breakpoint);
    }
    return breakpoints;
};
export const withBreakpoints = (allOrderedBreakpoints, until, currState, prevState, voucher, nextVoucher, fn) => {
    if (!fn) {
        throw new Error('No function provided');
    }
    let state = currState;
    const periodStart = voucher.parsedDate;
    const d = nextVoucher && nextVoucher.parsedDate;
    const periodEnd = !d || until < d ? until : d || until;
    if (periodStart > periodEnd) {
        return state;
    }
    let breakpointsInPeriod = getBreakpointsInPeriod(allOrderedBreakpoints, periodStart, periodEnd);
    let from = periodStart;
    for (let i = 0; i < breakpointsInPeriod.length; i++) {
        const breakpoint = breakpointsInPeriod[i];
        state = fn(currState, prevState, from, breakpoint.date, breakpoint.type);
        from = breakpoint.date;
    }
    // From last breakpoint to end of period
    state = fn(currState, prevState, from, periodEnd);
    return state;
};
export const getOrderedBreakpoints = (caseVouchers, until) => {
    let breakpoints = [];
    let containsDailyCompound = false;
    let containsMonthlyCompound = false;
    let containsQuarterlyCompound = false;
    // Interest start and end dates
    for (let i = 0; i < caseVouchers.length; i++) {
        const caseVoucher = caseVouchers[i];
        if (caseVoucher.parsedInterestStartDate) {
            breakpoints.push({
                type: BreakpointType.InterestStartDate,
                date: caseVoucher.parsedInterestStartDate,
            });
        }
        if (caseVoucher.parsedInterestEndDate) {
            breakpoints.push({
                type: BreakpointType.InterestEndDate,
                date: caseVoucher.parsedInterestEndDate,
            });
        }
        if (caseVoucher.parsedInterestPeriods != null) {
            for (const period of caseVoucher.parsedInterestPeriods) {
                breakpoints.push({
                    type: BreakpointType.InterestPeriodStart,
                    date: period.rateFrom,
                });
                if (period.rateTo !== OPEN_ENDED) {
                    breakpoints.push({
                        type: BreakpointType.InterestPeriodEnd,
                        date: period.rateTo,
                    });
                }
                if (period.voidedAt !== null) {
                    breakpoints.push({
                        type: BreakpointType.InterestPeriodVoid,
                        date: period.voidedAt,
                    });
                }
            }
        }
        if (caseVoucher.setting === VoucherTypeSetting.CompoundInterest) {
            if (caseVoucher.settingDetails.compoundingPeriod === CompoundingPeriod.Daily) {
                containsDailyCompound = true;
            }
            if (caseVoucher.settingDetails.compoundingPeriod ===
                CompoundingPeriod.Monthly) {
                containsMonthlyCompound = true;
            }
            if (caseVoucher.settingDetails.compoundingPeriod ===
                CompoundingPeriod.Quarterly) {
                containsQuarterlyCompound = true;
            }
        }
    }
    // Compounding periods
    const dailyCompoundingBreakpoints = containsDailyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestDailyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Daily), until, (d) => {
            const nextDay = new Date(d);
            nextDay.setUTCDate(nextDay.getUTCDate() + 1);
            return nextDay;
        }, (d) => {
            const startOfDay = new Date(d);
            startOfDay.setUTCHours(0, 0, 0, 0);
            return startOfDay;
        })
        : [];
    const monthlyCompoundingBreakpoints = containsMonthlyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestMonthlyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Monthly), until, (d) => {
            const nextMonth = new Date(d);
            nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
            return nextMonth;
        }, (d) => {
            const startOfMonth = new Date(d);
            startOfMonth.setUTCDate(1);
            startOfMonth.setUTCHours(0, 0, 0, 0);
            return startOfMonth;
        })
        : [];
    const quarterlyCompoundingBreakpoints = containsQuarterlyCompound
        ? getCompoundingBreakpoints(BreakpointType.InterestQuarterlyCompoundingPeriod, caseVouchers.filter((v) => v.setting === VoucherTypeSetting.CompoundInterest &&
            v.settingDetails.compoundingPeriod === CompoundingPeriod.Quarterly), until, (d) => {
            const nextQuarter = new Date(d);
            nextQuarter.setUTCMonth(nextQuarter.getUTCMonth() + 3);
            return nextQuarter;
        }, (d) => {
            // January, April, July or October, built in one step: setting the
            // month first would turn 31 May into "31 April", i.e. 1 May.
            return new Date(Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1));
        })
        : [];
    breakpoints = breakpoints.concat(dailyCompoundingBreakpoints, monthlyCompoundingBreakpoints, quarterlyCompoundingBreakpoints);
    return breakpoints.sort((a, b) => a.date - b.date);
};
// UTC month starts from the earliest case voucher until `until`, so callers of
// breakPointCallBack observe the accrual month by month.
export const getMonthStartBreakpoints = (caseVouchers, until) => getCompoundingBreakpoints(BreakpointType.MonthStart, caseVouchers, until, (d) => {
    const nextMonth = new Date(d);
    nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
    return nextMonth;
}, (d) => {
    const startOfMonth = new Date(d);
    startOfMonth.setUTCDate(1);
    startOfMonth.setUTCHours(0, 0, 0, 0);
    return startOfMonth;
});
const checkIfDateIsBefore = (compoundingDate, before) => {
    return compoundingDate <= parseDate(new Date(before).toISOString());
};
const getCompoundingBreakpoints = (type, caseVouchers, until, dateIncrementer, firstCompoundingDate) => {
    if (!caseVouchers.length) {
        return [];
    }
    const earliestVoucherDate = caseVouchers
        .map((v) => new Date(v.date))
        .sort((a, b) => a.getTime() - b.getTime())[0];
    let compoundingDate = firstCompoundingDate(earliestVoucherDate);
    let compoundingDates = [];
    while (checkIfDateIsBefore(compoundingDate.getTime(), until)) {
        compoundingDates.push({ date: compoundingDate.getTime(), type: type });
        compoundingDate = dateIncrementer(compoundingDate);
    }
    return compoundingDates;
};
//# sourceMappingURL=common.js.map